import { useCallback, useEffect, useMemo, useState } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, CanonicalPeriod, PERIODS } from '../common'
import floodScorePalette, { FloodScoreBadge } from '../common/flood/utils'
import { useHistory } from 'react-router-dom'
import { LoadingValue } from './PortfolioLoading'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'
import { useLocations } from '../../../hooks/locations'
import ClimateSectionTitle from '../common/ClimateSectionTitle'
import ClimateMetricCard from '../common/ClimateMetricCard'
import TooltipDataTable from '../common/TooltipDataTable'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatDamageTargetLabel,
  formatFloodSourceLabel,
  formatCompactNumber,
  parseMetricPointY,
  REVENUE_AAL_LABEL,
  TOTAL_AAL_LABEL,
  sumMetricNode,
} from '../common/financialMetrics'
import {
  formatPropertyCoverage,
  meanAggregateValues,
  sumAggregateValues,
  sumAvailableComponentValues,
  type AggregateSummary,
  type ComponentAggregateSummary,
} from '../common/portfolioAggregates'
import { useCurrentUser } from '../../../hooks/users'

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type PeriodicMetricMap = {
  values: Record<number, Partial<Record<CanonicalPeriod, number | null>>>
  status: Record<number, MetricState | null>
}

const FLOOD_DAMAGE_TARGETS = ['building', 'contents'] as const
const FLOOD_SOURCES = ['river', 'surface', 'coastal'] as const

function compactCurrency(value: number | null | undefined, currencySymbol: string) {
  if (value == null || Number.isNaN(value)) return '—'
  return `${currencySymbol}${formatCompactNumber(value, 1)} / year`
}

function fixed1(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)
}

function formatCoverageFraction(available: number, total: number) {
  return `${available} / ${total}`
}

function buildEntryMap(source: unknown): Record<number, any> {
  const out: Record<number, any> = {}

  if (source && typeof source === 'object') {
    Object.values(source as Record<string, any>).forEach(entry => {
      const propertyId = entry?.property_id
      if (typeof propertyId === 'number') {
        out[propertyId] = entry
      }
    })
  }

  return out
}

function getTierPeriodNode(entry: any, tierKey: string, period: CanonicalPeriod) {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return entry.data?.data?.[tierKey]?.[period] ?? null
}

function getFloodDamageAnnualValue(
  entry: any,
  tierKey: string,
  period: CanonicalPeriod,
  target?: string,
  source?: string,
) {
  const periodNode = getTierPeriodNode(entry, tierKey, period)
  const targetNode = target ? periodNode?.[target] : periodNode
  const sourceNode = source ? targetNode?.[source] : targetNode
  return sumMetricNode(sourceNode)
}

function getFloodRevenueAnnualValue(entry: any, tierKey: string, period: CanonicalPeriod, source?: string) {
  const periodNode = getTierPeriodNode(entry, tierKey, period)
  const sourceNode = source ? periodNode?.[source] : periodNode
  return sumMetricNode(sourceNode)
}

function renderHelpLines(lines: string[]) {
  return (
    <Stack spacing={0.5}>
      {lines.map(line => (
        <Typography key={line} variant="caption">
          {line}
        </Typography>
      ))}
    </Stack>
  )
}

function renderAggregateTooltip(
  summary: AggregateSummary,
  label: string,
  aggregationText: string,
  extraLines: string[] = [],
) {
  return renderHelpLines([
    `${label}: ${aggregationText}.`,
    `Coverage: ${formatPropertyCoverage(summary.contributors, summary.total)}.`,
    ...extraLines,
  ])
}

function pickScore(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return parseMetricPointY(entry.data?.data?.[tierKey]?.[period])
}

function pickAnnualMetric(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return sumMetricNode(entry.data?.data?.[tierKey]?.[period])
}

function buildPeriodicMetricMap(
  source: unknown,
  tierKey: string,
  extractor: (entry: any, tierKey: string, period: CanonicalPeriod) => number | null,
): PeriodicMetricMap {
  const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
  const status: Record<number, MetricState | null> = {}

  if (source && typeof source === 'object') {
    Object.values(source as Record<string, any>).forEach(entry => {
      const propertyId = entry?.property_id
      if (typeof propertyId !== 'number') return

      status[propertyId] = entry.state?.value ?? null
      const perPeriod: Partial<Record<CanonicalPeriod, number | null>> = {}
      PERIODS.forEach(period => {
        perPeriod[period] = extractor(entry, tierKey, period)
      })
      values[propertyId] = perPeriod
    })
  }

  return { values, status }
}

export default function PortfolioFlood({
  emissionTier,
  selectedPeriod,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const { currencySymbol } = useCurrentUser()

  const { data: scoreByProperty, isLoading: scoreLoading } = usePortfolioClimateData('fld:score', {
    groupId: groupId ?? undefined,
  })
  const { data: damageAalByProperty, isLoading: damageAalLoading } = usePortfolioClimateData(
    'fld:average_damage_loss',
    {
      groupId: groupId ?? undefined,
    },
  )
  const { data: revenueAalByProperty, isLoading: revenueAalLoading } = usePortfolioClimateData('fld:average_bi_loss', {
    groupId: groupId ?? undefined,
  })
  const { data: biDaysByProperty, isLoading: biDaysLoading } = usePortfolioClimateData('fld:average_bi_days', {
    groupId: groupId ?? undefined,
  })

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'fld', emissionTier)),
    [emissionTier, history],
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [])

  const tierKey = emissionTier as unknown as string
  const damageAalEntryMap = useMemo(() => buildEntryMap(damageAalByProperty), [damageAalByProperty])
  const revenueAalEntryMap = useMemo(() => buildEntryMap(revenueAalByProperty), [revenueAalByProperty])

  const scoreMetric = useMemo(
    () => buildPeriodicMetricMap(scoreByProperty, tierKey, pickScore),
    [scoreByProperty, tierKey],
  )
  const damageAalMetric = useMemo(
    () => buildPeriodicMetricMap(damageAalByProperty, tierKey, pickAnnualMetric),
    [damageAalByProperty, tierKey],
  )
  const revenueAalMetric = useMemo(
    () => buildPeriodicMetricMap(revenueAalByProperty, tierKey, pickAnnualMetric),
    [revenueAalByProperty, tierKey],
  )
  const biDaysMetric = useMemo(
    () => buildPeriodicMetricMap(biDaysByProperty, tierKey, pickAnnualMetric),
    [biDaysByProperty, tierKey],
  )

  const totalAalMetric = useMemo<PeriodicMetricMap>(() => {
    const values: PeriodicMetricMap['values'] = {}
    const status: PeriodicMetricMap['status'] = {}
    const propertyIds = new Set<number>([
      ...Object.keys(damageAalMetric.values).map(Number),
      ...Object.keys(revenueAalMetric.values).map(Number),
    ])

    propertyIds.forEach(propertyId => {
      const perPeriod: Partial<Record<CanonicalPeriod, number | null>> = {}
      PERIODS.forEach(period => {
        const damageValue = damageAalMetric.values[propertyId]?.[period] ?? null
        const revenueValue = revenueAalMetric.values[propertyId]?.[period] ?? null
        perPeriod[period] =
          typeof damageValue === 'number' || typeof revenueValue === 'number'
            ? (damageValue ?? 0) + (revenueValue ?? 0)
            : null
      })
      values[propertyId] = perPeriod

      const damageStatus = damageAalMetric.status[propertyId] ?? null
      const revenueStatus = revenueAalMetric.status[propertyId] ?? null
      status[propertyId] =
        damageStatus === MetricState.SUCCEEDED || revenueStatus === MetricState.SUCCEEDED
          ? MetricState.SUCCEEDED
          : damageStatus ?? revenueStatus
    })

    return { values, status }
  }, [damageAalMetric.status, damageAalMetric.values, revenueAalMetric.status, revenueAalMetric.values])

  const selectedPropertyIds = useMemo(() => {
    if (activePropertyIds) return activePropertyIds
    return (locations ?? []).map((location: any) => location.id)
  }, [activePropertyIds, locations])

  const kpis = useMemo(() => {
    return PERIODS.reduce(
      (out, period) => {
        const scoreSummary = meanAggregateValues(
          selectedPropertyIds,
          propertyId => scoreMetric.values[propertyId]?.[period] ?? null,
        )
        const damageAalSummary = sumAggregateValues(
          selectedPropertyIds,
          propertyId => damageAalMetric.values[propertyId]?.[period] ?? null,
        )
        const revenueAalSummary = sumAggregateValues(
          selectedPropertyIds,
          propertyId => revenueAalMetric.values[propertyId]?.[period] ?? null,
        )
        const biDaysSummary = meanAggregateValues(
          selectedPropertyIds,
          propertyId => biDaysMetric.values[propertyId]?.[period] ?? null,
        )
        const totalAalSummary = sumAvailableComponentValues(selectedPropertyIds, {
          structure: propertyId =>
            getFloodDamageAnnualValue(damageAalEntryMap[propertyId], tierKey, period, 'building'),
          contents: propertyId => getFloodDamageAnnualValue(damageAalEntryMap[propertyId], tierKey, period, 'contents'),
          revenue: propertyId => getFloodRevenueAnnualValue(revenueAalEntryMap[propertyId], tierKey, period),
        })

        out[period] = {
          scoreSummary,
          highCount: selectedPropertyIds.filter(propertyId => {
            const value = scoreMetric.values[propertyId]?.[period]
            return typeof value === 'number' && value >= 4
          }).length,
          totalAalSummary,
          damageAalSummary,
          revenueAalSummary,
          biDaysSummary,
        }

        return out
      },
      {} as Record<
        CanonicalPeriod,
        {
          scoreSummary: AggregateSummary
          highCount: number
          totalAalSummary: ComponentAggregateSummary
          damageAalSummary: AggregateSummary
          revenueAalSummary: AggregateSummary
          biDaysSummary: AggregateSummary
        }
      >,
    )
  }, [
    biDaysMetric.values,
    damageAalEntryMap,
    damageAalMetric.values,
    revenueAalEntryMap,
    revenueAalMetric.values,
    scoreMetric.values,
    selectedPropertyIds,
    tierKey,
  ])

  const selectedKpi = kpis[selectedPeriod]
  const totalAalHelpText = useMemo(
    () =>
      renderHelpLines([
        `${TOTAL_AAL_LABEL} = Structure Damage + Contents Damage + Revenue AAL.`,
        `Coverage: ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.contributors,
          selectedKpi.totalAalSummary.total,
        )}.`,
        `Structure available for ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.componentContributors.structure ?? 0,
          selectedKpi.totalAalSummary.total,
        )}.`,
        `Contents available for ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.componentContributors.contents ?? 0,
          selectedKpi.totalAalSummary.total,
        )}.`,
        `Revenue available for ${formatPropertyCoverage(
          selectedKpi.totalAalSummary.componentContributors.revenue ?? 0,
          selectedKpi.totalAalSummary.total,
        )}.`,
      ]),
    [selectedKpi.totalAalSummary],
  )
  const riskRatingHelpText = useMemo(
    () =>
      renderAggregateTooltip(selectedKpi.scoreSummary, 'Average Risk Rating', 'Average across available risk ratings'),
    [selectedKpi.scoreSummary],
  )
  const highRiskHelpText = useMemo(
    () =>
      renderHelpLines([
        'Count of selected properties rated High or Very High.',
        `Score coverage: ${formatPropertyCoverage(
          selectedKpi.scoreSummary.contributors,
          selectedKpi.scoreSummary.total,
        )}.`,
      ]),
    [selectedKpi.highCount, selectedKpi.scoreSummary],
  )
  const damageAalHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedKpi.damageAalSummary,
        DAMAGE_AAL_LABEL,
        'Summed across structure and contents damage where available',
      ),
    [selectedKpi.damageAalSummary],
  )
  const revenueAalHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedKpi.revenueAalSummary,
        REVENUE_AAL_LABEL,
        'Summed across available revenue-loss values',
      ),
    [selectedKpi.revenueAalSummary],
  )
  const biDaysHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedKpi.biDaysSummary,
        BI_DAYS_LABEL,
        'Average across available business interruption day values',
      ),
    [selectedKpi.biDaysSummary],
  )

  const getDamageBreakdownSummary = useCallback(
    (period: CanonicalPeriod, target: (typeof FLOOD_DAMAGE_TARGETS)[number], source: (typeof FLOOD_SOURCES)[number]) =>
      sumAggregateValues(selectedPropertyIds, propertyId =>
        getFloodDamageAnnualValue(damageAalEntryMap[propertyId], tierKey, period, target, source),
      ),
    [damageAalEntryMap, selectedPropertyIds, tierKey],
  )

  const getRevenueBySourceSummary = useCallback(
    (period: CanonicalPeriod, source: (typeof FLOOD_SOURCES)[number]) =>
      sumAggregateValues(selectedPropertyIds, propertyId =>
        getFloodRevenueAnnualValue(revenueAalEntryMap[propertyId], tierKey, period, source),
      ),
    [revenueAalEntryMap, selectedPropertyIds, tierKey],
  )

  const damageBreakdownHasData = useMemo(
    () =>
      FLOOD_DAMAGE_TARGETS.some(target =>
        FLOOD_SOURCES.some(source => getDamageBreakdownSummary(selectedPeriod, target, source).value != null),
      ),
    [getDamageBreakdownSummary, selectedPeriod],
  )

  const damageBreakdownHelpText = useMemo(
    () => (
      <TooltipDataTable
        title={`Properties contributing data per cell (${selectedPropertyIds.length} selected)`}
        columns={FLOOD_SOURCES.map(source => formatFloodSourceLabel(source))}
        rows={FLOOD_DAMAGE_TARGETS.map(target => ({
          label: formatDamageTargetLabel(target),
          values: FLOOD_SOURCES.map(source => {
            const summary = getDamageBreakdownSummary(selectedPeriod, target, source)
            return formatCoverageFraction(summary.contributors, summary.total)
          }),
        }))}
      />
    ),
    [getDamageBreakdownSummary, selectedPeriod, selectedPropertyIds.length],
  )

  const revenueBreakdownHasData = useMemo(
    () => FLOOD_SOURCES.some(source => getRevenueBySourceSummary(selectedPeriod, source).value != null),
    [getRevenueBySourceSummary, selectedPeriod],
  )

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'score',
        label: 'Risk Rating',
        perPeriod: true,
        width: 150,
        loading: scoreLoading,
        getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        renderScore: value => <FloodScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusScore',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: scoreLoading,
          getValue: propertyId => scoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        perPeriod: true,
        width: 170,
        loading: damageAalLoading || revenueAalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) =>
          totalAalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
      },
      {
        kind: 'currency',
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        perPeriod: true,
        width: 170,
        loading: damageAalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) =>
          damageAalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusDamageAal',
          label: `Status (${DAMAGE_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: damageAalLoading,
          getValue: propertyId => damageAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        perPeriod: true,
        width: 170,
        loading: revenueAalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) =>
          revenueAalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusRevenueAal',
          label: `Status (${REVENUE_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: revenueAalLoading,
          getValue: propertyId => revenueAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'bi_days',
        label: BI_DAYS_LABEL,
        perPeriod: true,
        width: 160,
        loading: biDaysLoading,
        format: 'fixed1',
        suffix: 'days/year',
        getValue: (propertyId, period) =>
          biDaysMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusBiDays',
          label: `Status (${BI_DAYS_LABEL})`,
          hiddenByDefault: true,
          loading: biDaysLoading,
          getValue: propertyId => biDaysMetric.status[propertyId] ?? null,
        },
      },
    ],
    [
      biDaysLoading,
      biDaysMetric.status,
      biDaysMetric.values,
      damageAalLoading,
      damageAalMetric.status,
      damageAalMetric.values,
      revenueAalLoading,
      revenueAalMetric.status,
      revenueAalMetric.values,
      scoreLoading,
      scoreMetric.status,
      scoreMetric.values,
      totalAalMetric.values,
    ],
  )

  const scoreNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'score',
      label: 'Risk Rating',
      perPeriod: true,
      loading: scoreLoading,
      getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period] ?? null,
      renderValue: value => <FloodScoreBadge score={value} size="dense" />,
    }),
    [scoreLoading, scoreMetric.values],
  )

  const totalAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'total_aal',
      label: TOTAL_AAL_LABEL,
      perPeriod: true,
      loading: damageAalLoading || revenueAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => totalAalMetric.values[propertyId]?.[period] ?? null,
    }),
    [damageAalLoading, revenueAalLoading, totalAalMetric.values],
  )

  const damageAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'damage_aal',
      label: DAMAGE_AAL_LABEL,
      perPeriod: true,
      loading: damageAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => damageAalMetric.values[propertyId]?.[period] ?? null,
    }),
    [damageAalLoading, damageAalMetric.values],
  )

  const revenueAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'revenue_aal',
      label: REVENUE_AAL_LABEL,
      perPeriod: true,
      loading: revenueAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => revenueAalMetric.values[propertyId]?.[period] ?? null,
    }),
    [revenueAalLoading, revenueAalMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: scoreNumericMetric,
        palette: floodScorePalette,
        labels: {
          1: 'Low',
          2: 'Moderate',
          3: 'Moderate to High',
          4: 'High',
          5: 'Very High',
        },
        normalize: value => Math.min(Math.max(Math.round(value), 1), 5),
      },
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        type: 'continuous',
        metric: totalAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        type: 'continuous',
        metric: damageAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        type: 'continuous',
        metric: revenueAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
    ],
    [damageAalNumericMetric, revenueAalNumericMetric, scoreNumericMetric, totalAalNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        type: 'scaled',
        metric: totalAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        type: 'scaled',
        metric: damageAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        type: 'scaled',
        metric: revenueAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [damageAalNumericMetric, revenueAalNumericMetric, totalAalNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        perPeriod: true,
        loading: damageAalLoading || revenueAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: (propertyId, period) => totalAalMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        perPeriod: true,
        loading: damageAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: (propertyId, period) => damageAalMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        perPeriod: true,
        loading: revenueAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: (propertyId, period) => revenueAalMetric.values[propertyId]?.[period] ?? null,
      },
      {
        key: 'bi_days',
        label: BI_DAYS_LABEL,
        perPeriod: true,
        loading: biDaysLoading,
        decimals: 1,
        suffix: 'days/year',
        getValue: (propertyId, period) => biDaysMetric.values[propertyId]?.[period] ?? null,
      },
    ],
    [
      biDaysLoading,
      biDaysMetric.values,
      damageAalLoading,
      damageAalMetric.values,
      revenueAalLoading,
      revenueAalMetric.values,
      totalAalMetric.values,
    ],
  )

  const annualMotionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems="center" pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.lg, width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }>
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title="Average Risk Rating"
              helpText={riskRatingHelpText}
              loading={scoreLoading}
              transitionKey={annualMotionKey}>
              <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                <FloodScoreBadge score={selectedKpi.scoreSummary.value} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title="High / Very High Risk Ratings"
              helpText={highRiskHelpText}
              loading={scoreLoading}
              transitionKey={annualMotionKey}>
              <LoadingValue loading={scoreLoading} variant="rounded" width={60} height={24}>
                <Chip label={selectedKpi.highCount} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title={TOTAL_AAL_LABEL}
              helpText={totalAalHelpText}
              loading={damageAalLoading || revenueAalLoading}
              transitionKey={annualMotionKey}>
              <LoadingValue loading={damageAalLoading || revenueAalLoading} variant="rounded" width={140} height={24}>
                <Chip label={compactCurrency(selectedKpi.totalAalSummary.value, currencySymbol)} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <ClimateSectionTitle
                title={FINANCIAL_BREAKDOWN_TITLE}
                subtitle="Damage and revenue components of annualised losses across the selected properties"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={DAMAGE_AAL_LABEL}
                    helpText={damageAalHelpText}
                    loading={damageAalLoading}
                    transitionKey={annualMotionKey}>
                    <LoadingValue loading={damageAalLoading} variant="rounded" width={140} height={24}>
                      <Chip label={compactCurrency(selectedKpi.damageAalSummary.value, currencySymbol)} />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={REVENUE_AAL_LABEL}
                    helpText={revenueAalHelpText}
                    loading={revenueAalLoading}
                    transitionKey={annualMotionKey}>
                    <LoadingValue loading={revenueAalLoading} variant="rounded" width={140} height={24}>
                      <Chip label={compactCurrency(selectedKpi.revenueAalSummary.value, currencySymbol)} />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={BI_DAYS_LABEL}
                    helpText={biDaysHelpText}
                    loading={biDaysLoading}
                    transitionKey={annualMotionKey}>
                    <LoadingValue loading={biDaysLoading} variant="rounded" width={120} height={24}>
                      <Chip
                        label={
                          selectedKpi.biDaysSummary.value != null
                            ? `${fixed1(selectedKpi.biDaysSummary.value)} days/year`
                            : '—'
                        }
                      />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <ClimateMetricCard
                    title="Damage Breakdown"
                    subtitle="Structure and contents split across River, Surface, and Coastal flooding"
                    helpText={damageBreakdownHelpText}
                    loading={damageAalLoading}
                    transitionKey={annualMotionKey}
                    contentSx={{ height: '100%' }}>
                    {!damageBreakdownHasData ? (
                      <Typography variant="body2" color="text.secondary">
                        No damage breakdown is available for this selection.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell />
                            {FLOOD_SOURCES.map(source => (
                              <TableCell key={source} align="right">
                                {formatFloodSourceLabel(source)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {FLOOD_DAMAGE_TARGETS.map(target => (
                            <TableRow key={target}>
                              <TableCell component="th" scope="row">
                                {formatDamageTargetLabel(target)}
                              </TableCell>
                              {FLOOD_SOURCES.map(source => {
                                const summary = getDamageBreakdownSummary(selectedPeriod, target, source)
                                return (
                                  <TableCell key={`${target}-${source}`} align="right">
                                    <Tooltip
                                      title={renderHelpLines([
                                        'Aggregation: sum across contributing properties.',
                                        `Coverage: ${formatPropertyCoverage(summary.contributors, summary.total)}.`,
                                      ])}
                                      arrow
                                      enterTouchDelay={0}>
                                      <Box component="span">{compactCurrency(summary.value, currencySymbol)}</Box>
                                    </Tooltip>
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </ClimateMetricCard>
                </Grid>

                <Grid item xs={12} md={5}>
                  <ClimateMetricCard
                    title="Revenue Loss Breakdown"
                    subtitle="Annual interruption-related revenue loss across flood sources"
                    loading={revenueAalLoading}
                    transitionKey={annualMotionKey}
                    contentSx={{ height: '100%' }}>
                    {!revenueBreakdownHasData ? (
                      <Typography variant="h4">—</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {FLOOD_SOURCES.map(source => {
                          const summary = getRevenueBySourceSummary(selectedPeriod, source)
                          return (
                            <Stack key={source} direction="row" justifyContent="space-between" alignItems="baseline">
                              <Typography variant="body2" color="text.secondary">
                                {formatFloodSourceLabel(source)}
                              </Typography>
                              <Tooltip
                                title={renderHelpLines([
                                  'Aggregation: sum across contributing properties.',
                                  `Coverage: ${formatPropertyCoverage(summary.contributors, summary.total)}.`,
                                ])}
                                arrow
                                enterTouchDelay={0}>
                                <Box component="span">
                                  <Typography variant="body2">
                                    {compactCurrency(summary.value, currencySymbol)}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Stack>
                          )
                        })}
                      </Stack>
                    )}
                  </ClimateMetricCard>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="score"
          defaultRadiusKey="default"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'score', period: selectedPeriod, sort: 'desc' }}
        />

      </Stack>
    </Stack>
  )
}
